import { Link } from "react-router-dom";

const HERO_BG =
  "https://readdy.ai/api/search-image?query=dramatic%20aerial%20view%20of%20London%20financial%20district%20at%20golden%20hour%2C%20Canary%20Wharf%20skyline%2C%20River%20Thames%20winding%20below%2C%20luxury%20high-rise%20towers%20catching%20warm%20amber%20sunset%20light%2C%20financial%20capital%20city%2C%20deep%20navy%20sky%20at%20top%20fading%20to%20rich%20gold%20at%20horizon%2C%20ultra%20wide%20cinematic%20real%20estate%20investment%20photography%2C%20no%20people%2C%20timeless%20authoritative%20atmosphere&width=1600&height=900&seq=801&orientation=landscape";

const keyTerms = [
  { icon: "ri-percent-line", label: "10.00% p.a. Target" },
  { icon: "ri-calendar-line", label: "Monthly Income" },
  { icon: "ri-time-line", label: "5-Year Term" },
  { icon: "ri-building-2-line", label: "UK Social Housing" },
  { icon: "ri-stock-line", label: "NASDAQ Dubai Bonds" },
];

export default function HeroSection() {
  return (
    <section
      className="relative w-full flex flex-col justify-end overflow-hidden"
      style={{
        minHeight: "100svh",
        backgroundImage: `url('${HERO_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

      {/* Floating stat cards — desktop only */}
      <div className="absolute top-32 right-6 md:right-12 hidden lg:flex flex-col items-end gap-3">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4 text-white text-right">
          <p className="text-xs text-white/60 mb-1">Target Annual Return</p>
          <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>10.00% p.a.</p>
        </div>
        <div className="bg-[#C9A84C]/90 backdrop-blur-sm rounded-2xl px-5 py-4 text-[#071A2E] text-right">
          <p className="text-xs font-semibold mb-1">Investment Term</p>
          <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>5 Years</p>
        </div>
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4 text-white text-right">
          <p className="text-xs text-white/60 mb-1">Also Available</p>
          <p className="text-sm font-bold">NASDAQ Dubai Bonds</p>
        </div>
      </div>

      {/* Hero Content */}
      <div
        className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-0"
        style={{ paddingBottom: "max(5rem, calc(5rem + env(safe-area-inset-bottom)))" }}
      >
        <div className="max-w-2xl">
          {/* Badge */}
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/20 border border-[#C9A84C]/40 text-[#C9A84C] text-[10px] md:text-xs font-semibold px-3 md:px-4 py-1.5 rounded-full mb-5 md:mb-6 backdrop-blur-sm">
            <i className="ri-shield-check-line text-sm leading-none" />
            <span className="hidden sm:inline">Section 21 FSMA 2000 — </span>Regulated Financial Promotion
          </span>

          {/* Headline */}
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-4 md:mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Social Housing
            <br />
            <span className="text-[#C9A84C]">Structured Product</span>
          </h1>

          {/* Subtext */}
          <p className="text-white/80 text-sm md:text-lg leading-relaxed mb-3 md:mb-4 max-w-xl">
            A 5-year, income-generating investment backed by UK assisted living assets — targeting{" "}
            <strong className="text-white">10.00% per annum</strong>, distributed monthly.
          </p>
          <p className="text-white/45 text-xs leading-relaxed mb-7 md:mb-9 max-w-xl">
            Intended solely for Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Investments involve risk. Capital may be at risk.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/social-housing"
              className="inline-flex items-center justify-center gap-2 px-7 md:px-8 py-4 rounded-full font-semibold text-sm hover:brightness-110 transition-all cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", color: "#071A2E" }}
            >
              Explore the Product
              <i className="ri-arrow-right-line text-sm leading-none" />
            </Link>
            <button
              type="button"
              onClick={() => { const el = document.querySelector("#vapi-widget-floating-button"); if (el instanceof HTMLElement) el.click(); }}
              className="inline-flex items-center justify-center gap-2 px-7 md:px-8 py-4 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold text-sm hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-sm leading-none" />
              Schedule a Call
            </button>
          </div>
        </div>

        {/* Key terms strip */}
        <div className="mt-8 md:mt-10 flex flex-wrap gap-2 md:gap-3 max-w-2xl">
          {keyTerms.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-3 md:px-4 py-2 md:py-2.5 text-white text-[10px] md:text-xs font-medium"
            >
              <div className="w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center">
                <i className={`${item.icon} leading-none`} />
              </div>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
